/*
 * The page's addresses around a room match, not the room code: the query string that joins a
 * duel and the one that comes back to the front page. The code (its alphabet, minting and
 * cleaning) is `src/net/roomCode.ts`, shared with the server.
 */

/**
 * The address of a game joined from the front page: its room, its seat, the other knobs kept.
 * `seed` goes (the room is the seed) and so do the mock lobby's knobs; `?lobby=server` stays, so
 * the way back to the page finds the server again (B3.6). A duel is the King's seat; K2: a company
 * duel names its mode and the seat asked for, so a reload whose room is gone plays the company
 * offline from that seat, as a duel's plays the duel against scripts. K4: the party word the
 * player sat with too, so the address it copies to a friend sits the friend in its kingdom.
 * K6.1: two against two names its mode too, its seat the King's, the only one it has; K7.1:
 * so does the war.
 */
export function roomAddress(
  search: string,
  code: string,
  mode: "duel" | "company" | "allies" | "war" = "duel",
  seat: "king" | "soldier" = "king",
  party?: string,
): string {
  const params = new URLSearchParams(search);
  for (const name of ["seed", "countdown", "mode"]) {
    params.delete(name);
  }
  if (params.get("lobby") !== "server") {
    params.delete("lobby");
  }
  params.set("room", code);
  if (mode !== "duel") {
    params.set("mode", mode);
  }
  params.set("seat", mode === "company" ? seat : "king");
  if (party !== undefined) {
    params.set("party", party);
  }
  return `?${params.toString()}`;
}

/** The longest party word the page sends (the room's own limit is the wire's). */
export const MAX_PARTY_CHARS = 32;

/** K3: a party word as typed: trimmed, cut to the wire's length, and none when empty. */
export function partyOf(text: string | null | undefined): string | undefined {
  const word = text?.trim().slice(0, MAX_PARTY_CHARS);
  return word ? word : undefined;
}

/**
 * K2: the party the address names (`?party=`, in every build): friends who open the front page
 * with one word sit in one kingdom of the game they join. A link carries it; since K3 it fills the
 * company card's field, where a party is typed.
 */
export function partyOfQuery(search: string): string | undefined {
  return partyOf(new URLSearchParams(search).get("party"));
}

/** The front page again after a room match (B3.6): the game, its mode and the seat go, the knobs stay. */
export function landingAddress(search: string): string {
  const params = new URLSearchParams(search);
  for (const name of ["room", "mode", "seat"]) {
    params.delete(name);
  }
  return `?${params.toString()}`;
}
